import React, { useMemo } from "react";
import "./AnimatedSystemMap.css";

/**
 * AnimatedSystemMap — a cinematic constellation of RAW-DATA's brain,
 * agents, channels, and integrations. Inspired by the owner's reference
 * screenshot: color-coded clusters, geometric node shapes, electricity
 * pulses traveling along the connecting edges, and spinning radar nodes.
 *
 * Architecture is mapped one-to-one against the actual stack:
 *   • BRAIN (center): GPT-5.2 primary + Claude Sonnet 4.5 + Gemini 3 Pro standby
 *   • ORCHESTRATOR (left): RAW Company router
 *   • AGENTS (top-left cluster): Tom · Sarah · Eva · Scout · Dobby
 *   • CHANNELS (bottom): Voice · Slack · iMessage
 *   • INTEGRATIONS (right cluster): Capital · Stripe · Tavily · ElevenLabs · …
 *   • SOCIAL (bottom-right): Instagram · TikTok · YouTube · Facebook
 *   • DATA (top-right radar cluster): Calendar · Gmail · Drive · GitHub
 */

const W = 1200;
const H = 720;

// Node colors map to the screenshot's palette.
const PALETTE = {
  brain:    "#FFD24A", // gold core
  orch:     "#A78BFA", // purple
  agent:    "#7CFFB7", // mint green
  channel:  "#FF7FB1", // magenta
  integ:    "#FFC555", // yellow
  social:   "#FF7A7A", // coral
  data:     "#9AE6FF", // cyan
};

const NODES = [
  // --- CORE ---
  { id: "brain",     label: "R.A.W. BRAIN",      sub: "GPT-5.2",     x: 600, y: 360, group: "brain",   shape: "core",   r: 60 },

  // --- ORCHESTRATOR ---
  { id: "orch",      label: "ORCHESTRATOR",      sub: "router",      x: 360, y: 360, group: "orch",    shape: "polyhedron", r: 38 },

  // --- AGENTS (left cluster) ---
  { id: "tom",       label: "TOM",     sub: "dev",       x: 165, y: 180, group: "agent", shape: "dotsphere", r: 26 },
  { id: "sarah",     label: "SARAH",   sub: "support",   x: 70,  y: 290, group: "agent", shape: "dotsphere", r: 26 },
  { id: "eva",       label: "EVA",     sub: "content",   x: 70,  y: 430, group: "agent", shape: "dotsphere", r: 26 },
  { id: "scout",     label: "SCOUT",   sub: "research",  x: 165, y: 540, group: "agent", shape: "dotsphere", r: 26 },
  { id: "dobby",     label: "DOBBY",   sub: "ads/growth",x: 290, y: 600, group: "agent", shape: "dotsphere", r: 26 },

  // --- CHANNELS (bottom row) ---
  { id: "voice",     label: "VOICE",     sub: "always-on", x: 470, y: 640, group: "channel", shape: "ring", r: 28 },
  { id: "slack",     label: "SLACK",     sub: "team",      x: 600, y: 680, group: "channel", shape: "ring", r: 28 },
  { id: "imessage",  label: "iMESSAGE",  sub: "owner",     x: 730, y: 640, group: "channel", shape: "ring", r: 28 },

  // --- INTEGRATIONS (right middle cluster) ---
  { id: "capital",   label: "CAPITAL.COM", sub: "trading",   x: 870, y: 420, group: "integ", shape: "hex", r: 30 },
  { id: "stripe",    label: "STRIPE",      sub: "payments",  x: 1000, y: 480, group: "integ", shape: "hex", r: 26 },
  { id: "tavily",    label: "TAVILY",      sub: "web search",x: 1080, y: 360, group: "integ", shape: "hex", r: 26 },
  { id: "eleven",    label: "ELEVENLABS",  sub: "voice",     x: 1000, y: 250, group: "integ", shape: "hex", r: 26 },

  // --- DATA (top-right radar cluster) ---
  { id: "gmail",     label: "GMAIL",       sub: "inbox",     x: 870, y: 130, group: "data",  shape: "radar", r: 30 },
  { id: "calendar",  label: "CALENDAR",    sub: "days",      x: 990, y: 110, group: "data",  shape: "radar", r: 26 },
  { id: "drive",     label: "DRIVE",       sub: "files",     x: 1090, y: 200, group: "data",  shape: "radar", r: 26 },
  { id: "github",    label: "GITHUB",      sub: "code",      x: 760, y: 90,  group: "data",  shape: "radar", r: 26 },

  // --- SOCIAL (bottom-right) ---
  { id: "instagram", label: "INSTAGRAM",   sub: "social",    x: 870, y: 580, group: "social", shape: "hex", r: 26 },
  { id: "tiktok",    label: "TIKTOK",      sub: "social",    x: 1000, y: 620, group: "social", shape: "hex", r: 26 },
  { id: "youtube",   label: "YOUTUBE",     sub: "video",     x: 1080, y: 510, group: "social", shape: "hex", r: 26 },
  { id: "facebook",  label: "FACEBOOK",    sub: "social",    x: 950, y: 560, group: "social", shape: "hex", r: 22 },

  // --- TOP CENTER: standby LLMs ---
  { id: "claude",    label: "CLAUDE 4.5",  sub: "standby",   x: 500, y: 130, group: "brain",  shape: "diamond", r: 22 },
  { id: "gemini",    label: "GEMINI 3 PRO",sub: "standby",   x: 700, y: 130, group: "brain",  shape: "diamond", r: 22 },
];

// Edges define the data flow. Each edge has a "pulse" speed (s) so the
// electricity travels at different rates → feels alive, not metronomic.
const EDGES = [
  // Orchestrator ↔ Brain
  { from: "orch",     to: "brain",    speed: 2.6, intensity: 1.0 },
  // Brain ↔ standby LLMs
  { from: "brain",    to: "claude",   speed: 4.2, intensity: 0.5 },
  { from: "brain",    to: "gemini",   speed: 4.5, intensity: 0.5 },
  // Agents → Orchestrator
  { from: "tom",      to: "orch",     speed: 3.0 },
  { from: "sarah",    to: "orch",     speed: 3.4 },
  { from: "eva",      to: "orch",     speed: 3.8 },
  { from: "scout",    to: "orch",     speed: 3.2 },
  { from: "dobby",    to: "orch",     speed: 3.6 },
  // Channels → Brain
  { from: "voice",    to: "brain",    speed: 2.2, intensity: 1.0 },
  { from: "slack",    to: "brain",    speed: 3.0 },
  { from: "imessage", to: "brain",    speed: 3.0 },
  // Brain → Integrations
  { from: "brain",    to: "capital",  speed: 2.8 },
  { from: "brain",    to: "stripe",   speed: 4.0 },
  { from: "brain",    to: "tavily",   speed: 3.4 },
  { from: "brain",    to: "eleven",   speed: 3.0 },
  // Brain → Data (radar cluster)
  { from: "brain",    to: "gmail",    speed: 3.6 },
  { from: "brain",    to: "calendar", speed: 3.8 },
  { from: "brain",    to: "drive",    speed: 4.2 },
  { from: "brain",    to: "github",   speed: 4.0 },
  // Brain → Social
  { from: "brain",    to: "instagram",speed: 3.4 },
  { from: "brain",    to: "tiktok",   speed: 3.6 },
  { from: "brain",    to: "youtube",  speed: 3.8 },
  { from: "brain",    to: "facebook", speed: 4.0 },
];

function AnimatedSystemMap() {
  const nodeMap = useMemo(() => Object.fromEntries(NODES.map((n) => [n.id, n])), []);

  return (
    <div className="anim-sys-map" data-testid="animated-system-map">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="anim-sys-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {/* Grid pattern */}
          <pattern id="map-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,184,0,0.05)" strokeWidth="0.5" />
          </pattern>
          {/* Radial glow for the brain core */}
          <radialGradient id="brain-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFD24A" stopOpacity="1" />
            <stop offset="50%" stopColor="#FFB800" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#FFB800" stopOpacity="0" />
          </radialGradient>
          {/* Soft glow filter for nodes */}
          <filter id="node-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background grid */}
        <rect width={W} height={H} fill="url(#map-grid)" />

        {/* Edges — drawn first so nodes overlay */}
        {EDGES.map((e, i) => {
          const a = nodeMap[e.from];
          const b = nodeMap[e.to];
          if (!a || !b) return null;
          const color = PALETTE[b.group] || "#FFB800";
          const id = `edge-${i}`;
          return (
            <g key={id} className="anim-edge" style={{ "--edge-speed": `${e.speed}s` }}>
              {/* Base wire (low opacity) */}
              <line
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke={color} strokeWidth="1" opacity={0.15}
              />
              {/* Animated electricity pulse — a short dash that travels the line */}
              <line
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke={color}
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeDasharray="6 200"
                opacity={(e.intensity ?? 0.8)}
                className="edge-pulse"
                filter="url(#node-glow)"
              />
            </g>
          );
        })}

        {/* Nodes */}
        {NODES.map((n) => (
          <Node key={n.id} node={n} />
        ))}
      </svg>

      <div className="anim-sys-legend">
        {[
          { k: "Brain · LLMs",   c: PALETTE.brain },
          { k: "Orchestrator",   c: PALETTE.orch  },
          { k: "Agents",         c: PALETTE.agent },
          { k: "Channels",       c: PALETTE.channel },
          { k: "Integrations",   c: PALETTE.integ },
          { k: "Data · Cloud",   c: PALETTE.data },
          { k: "Social",         c: PALETTE.social },
        ].map((l) => (
          <span key={l.k} className="legend-chip">
            <span className="legend-dot" style={{ background: l.c }} />
            {l.k}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Per-node renderer — different geometric shapes per category         */
/* Owner asked: make agent nodes clickable so tapping one opens the    */
/* edit modal for that agent. We dispatch the same custom event that   */
/* App.js already listens for (`rawco-open-agent-studio`).             */
/* ------------------------------------------------------------------ */
function nodeClickHandler(node) {
  // Only agents / orchestrator are "edit-able"; everything else routes
  // to a settings tab for now (we can expand per-integration later).
  if (node.group === "agent") {
    return () => {
      try {
        window.dispatchEvent(new CustomEvent("rawco-open-agent-studio", {
          detail: { agent: node.label },
        }));
      } catch { /* ignore */ }
    };
  }
  if (node.group === "integ" || node.group === "data" || node.group === "social") {
    return () => {
      try {
        window.dispatchEvent(new CustomEvent("rawco-open-settings", {
          detail: { tab: "integrations" },
        }));
      } catch { /* ignore */ }
    };
  }
  return undefined;
}

function Node({ node }) {
  const color = PALETTE[node.group] || "#FFB800";
  const onClick = nodeClickHandler(node);
  const cursor = onClick ? "pointer" : "default";
  const commonProps = {
    transform: `translate(${node.x},${node.y})`,
    "data-testid": `map-node-${node.id}`,
    onClick,
    style: { cursor },
  };

  if (node.shape === "core") {
    return (
      <g {...commonProps} className="node-core">
        {/* Outer ring (slow spin) */}
        <g className="spin-slow">
          <circle r={node.r + 18} fill="none" stroke={color} strokeWidth="0.6" opacity="0.45" />
          <circle r={node.r + 18} fill="none" stroke={color} strokeWidth="2" strokeDasharray="2 30" opacity="0.9" />
        </g>
        {/* Mid ring (counter-spin) */}
        <g className="spin-fast-reverse">
          <circle r={node.r + 8} fill="none" stroke={color} strokeWidth="0.6" opacity="0.5" strokeDasharray="1 6" />
        </g>
        <circle r={node.r + 28} fill="url(#brain-glow)" opacity="0.35" />
        <circle r={node.r} fill={color} opacity="0.18" />
        <circle r={node.r * 0.65} fill={color} opacity="0.55" className="pulse-fade" />
        <circle r={node.r * 0.35} fill="#FFFFFF" opacity="0.85" />
        <Label node={node} color={color} dy={node.r + 30} />
      </g>
    );
  }

  if (node.shape === "polyhedron") {
    // Wireframe diamond/octahedron
    const r = node.r;
    return (
      <g {...commonProps}>
        <g className="spin-fast">
          <polygon
            points={`0,${-r} ${r},0 0,${r} ${-r},0`}
            fill={color} fillOpacity="0.12"
            stroke={color} strokeWidth="1"
            filter="url(#node-glow)"
          />
          <polygon
            points={`0,${-r * 0.55} ${r * 0.55},0 0,${r * 0.55} ${-r * 0.55},0`}
            fill="none" stroke={color} strokeWidth="0.7" opacity="0.7"
          />
          <line x1={-r} y1="0" x2={r} y2="0" stroke={color} strokeWidth="0.5" opacity="0.5" />
          <line x1="0" y1={-r} x2="0" y2={r} stroke={color} strokeWidth="0.5" opacity="0.5" />
        </g>
        <circle r="3" fill={color} className="pulse-fade" />
        <Label node={node} color={color} dy={r + 18} />
      </g>
    );
  }

  if (node.shape === "dotsphere") {
    // Cluster of dots arranged as a sphere illusion
    const r = node.r;
    const dots = [];
    for (let i = 0; i < 24; i++) {
      const ang = (i / 24) * Math.PI * 2;
      const ring = (i % 3) + 1;
      const rad = r * (0.55 + ring * 0.13);
      dots.push({ x: Math.cos(ang) * rad, y: Math.sin(ang) * rad * 0.6, s: 1.3 - ring * 0.25, o: 0.4 + ring * 0.2 });
    }
    return (
      <g {...commonProps}>
        <title>Click to edit {node.label}</title>
        <g className="spin-medium">
          <circle r={r} fill={color} fillOpacity="0.06" stroke={color} strokeOpacity="0.4" strokeWidth="0.6" />
          {dots.map((d, i) => (
            <circle key={i} cx={d.x} cy={d.y} r={d.s} fill={color} opacity={d.o} />
          ))}
        </g>
        <circle r="2.5" fill={color} className="pulse-fade" />
        <Label node={node} color={color} dy={r + 14} />
      </g>
    );
  }

  if (node.shape === "ring") {
    return (
      <g {...commonProps}>
        <circle r={node.r} fill="none" stroke={color} strokeWidth="1.4" opacity="0.85" filter="url(#node-glow)" />
        <circle r={node.r * 0.55} fill={color} fillOpacity="0.18" stroke={color} strokeWidth="0.6" />
        <circle r="2.5" fill={color} className="pulse-fade" />
        <Label node={node} color={color} dy={node.r + 14} />
      </g>
    );
  }

  if (node.shape === "hex") {
    const r = node.r;
    const pts = [];
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      pts.push(`${Math.cos(a) * r},${Math.sin(a) * r}`);
    }
    return (
      <g {...commonProps}>
        <polygon points={pts.join(" ")} fill={color} fillOpacity="0.12" stroke={color} strokeWidth="1.1" filter="url(#node-glow)" />
        <polygon points={pts.join(" ")} fill="none" stroke={color} strokeWidth="0.4" opacity="0.5" transform="scale(0.55)" />
        <circle r="2.5" fill={color} className="pulse-fade" />
        <Label node={node} color={color} dy={r + 14} />
      </g>
    );
  }

  if (node.shape === "diamond") {
    const r = node.r;
    return (
      <g {...commonProps}>
        <polygon points={`0,${-r} ${r * 0.8},0 0,${r} ${-r * 0.8},0`} fill={color} fillOpacity="0.1" stroke={color} strokeWidth="1" />
        <circle r="2" fill={color} className="pulse-fade" />
        <Label node={node} color={color} dy={r + 14} />
      </g>
    );
  }

  if (node.shape === "radar") {
    const r = node.r;
    return (
      <g {...commonProps}>
        {/* Concentric radar circles */}
        <circle r={r} fill="none" stroke={color} strokeOpacity="0.4" strokeWidth="0.6" />
        <circle r={r * 0.7} fill="none" stroke={color} strokeOpacity="0.55" strokeWidth="0.6" />
        <circle r={r * 0.4} fill="none" stroke={color} strokeOpacity="0.75" strokeWidth="0.7" />
        <line x1={-r} y1="0" x2={r} y2="0" stroke={color} strokeOpacity="0.3" strokeWidth="0.4" />
        <line x1="0" y1={-r} x2="0" y2={r} stroke={color} strokeOpacity="0.3" strokeWidth="0.4" />
        {/* Rotating sweep wedge */}
        <g className="spin-medium">
          <path
            d={`M 0 0 L ${r} 0 A ${r} ${r} 0 0 1 ${r * Math.cos(Math.PI / 3.5)} ${r * Math.sin(Math.PI / 3.5)} Z`}
            fill={color}
            fillOpacity="0.18"
          />
          <line x1="0" y1="0" x2={r} y2="0" stroke={color} strokeWidth="1.2" />
        </g>
        <circle r="2.2" fill={color} />
        <Label node={node} color={color} dy={r + 14} />
      </g>
    );
  }

  return null;
}

function Label({ node, color, dy }) {
  return (
    <g>
      <text
        x="0" y={dy}
        textAnchor="middle"
        fill={color}
        fontSize="9.5"
        fontFamily="Share Tech Mono, monospace"
        letterSpacing="0.12em"
        opacity="0.95"
      >
        {node.label}
      </text>
      {node.sub && (
        <text
          x="0" y={dy + 11}
          textAnchor="middle"
          fill="#fff"
          fontSize="7.5"
          fontFamily="JetBrains Mono, monospace"
          opacity="0.45"
        >
          {node.sub}
        </text>
      )}
    </g>
  );
}

export default React.memo(AnimatedSystemMap);
